(function () {

class SplitContent extends HTMLElement {
  constructor() {
    super();
    
    // Core properties
    this.sliderComponent = this.querySelector('slider-component');
    this.currentSlideIndex = 1;
    this.isInViewport = false;
    this.lastVisibilityUpdate = null; // Track last visibility update
    
    // Arrow button tracking
    this.lastClickedArrow = null;
    
    // Intersection observer for section
    this.sectionObserver = null;
    
    // Theme editor detection
    this.isThemeEditor = window.Shopify && window.Shopify.designMode;
    
    // Video management
    this.videoElements = [];
    
    // Bind methods
    this.handleSlideChanged = this.handleSlideChanged.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleVideoEnded = this.handleVideoEnded.bind(this);
    this.handleSectionIntersection = this.handleSectionIntersection.bind(this);
    this.handleResize = this.handleResize.bind(this);
    
    this.init();
  }

  init() {
    this.setupEventListeners();
    this.initializeVideos();
    
    // Set initial slide visibility
    this.updateSlideVisibility(1);
    
    // Set initial tab order
    this.updateTabIndex();
    
    // Setup section observer
    this.setupSectionObserver();
  }

  setupSectionObserver() {
    if (!this.sectionObserver) {
      this.sectionObserver = new IntersectionObserver(this.handleSectionIntersection, {
        threshold: 0.1,
        rootMargin: '50px'
      });
      
      this.sectionObserver.observe(this);
    }
  }

  handleSectionIntersection(entries) {
    entries.forEach(entry => {
      this.isInViewport = entry.isIntersecting;
      
      if (!this.isInViewport) {
        this.pauseAllVideos();
      } else {
        // Section entered viewport - play visible videos
        this.playVisibleVideos();
      }
    });
  }

  pauseAllVideos() {
    this.videoElements.forEach(videoData => {
      if (videoData.type === 'native') {
        videoData.element.pause();
      }
    });
  }

  playVisibleVideos() {
    this.videoElements.forEach(videoData => {
      if (videoData.type === 'native') {
        const videoSlide = videoData.container.closest('.split-content__item');
        if (videoSlide) {
          const computedStyle = window.getComputedStyle(videoSlide);
          const isVisible = computedStyle.visibility !== 'hidden';
          
          if (isVisible && this.isInViewport) {
            videoData.element.play().catch(() => {});
          }
        }
      }
    });
  }

  setupEventListeners() {
    if (this.sliderComponent) {
      this.sliderComponent.addEventListener('slideChanged', this.handleSlideChanged);
    }
    
    this.setupArrowClickHandlers();
    
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('resize', this.handleResize);
    
    if (this.isThemeEditor) {
      this.setupThemeEditorListeners();
    }
  }

  handleResize() {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.lastVisibilityUpdate = null; // Clear cache on resize
      this.updateSlideVisibility(this.currentSlideIndex);
      this.updateTabIndex();
      this.playVisibleVideos();
    }, 150);
  }

  setupArrowClickHandlers() {
    const arrowButtons = this.querySelectorAll('.slider-arrow, .slider-button--prev, .slider-button--next');
    
    arrowButtons.forEach(arrow => {
      arrow.addEventListener('click', (e) => {
        this.lastClickedArrow = e.currentTarget;
        
        setTimeout(() => {
          if (this.lastClickedArrow) {
            this.lastClickedArrow.focus();
          }
        }, 100);
      });
    });
  }

  setupThemeEditorListeners() {
    document.addEventListener('shopify:section:unload', (e) => {
      if (e.detail.sectionId === this.closest('.shopify-section')?.id) {
        this.cleanup();
      }
    });
  }

  initializeVideos() {
    const videoBlocks = this.querySelectorAll('[data-slide-video]');
    
    videoBlocks.forEach((block) => {
      const video = block.querySelector('video');
      const slideIndex = parseInt(block.getAttribute('data-slide-index'));
      
      if (video) {
        video.loop = true;
        video.muted = true;
        video.playsInline = true;
        video.preload = 'metadata';
        
        video.addEventListener('ended', this.handleVideoEnded);
        
        this.videoElements.push({
          type: 'native',
          element: video,
          container: block,
          index: slideIndex
        });
      }
    });
  }

  handleVideoEnded(e) {
    const video = e.target;
    video.currentTime = 0;
    video.play().catch(() => {});
  }

  handleSlideChanged(e) {
    const { currentPage } = e.detail;
    this.currentSlideIndex = currentPage;
    
    // Update visibility first
    this.updateSlideVisibility(currentPage);
    
    // Then update tab order
    this.updateTabIndex();
    
    // Update ARIA
    this.updateARIA(currentPage);
    
    // Play/pause videos based on new visibility
    this.playVisibleVideos();
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.pauseAllVideos();
    } else if (this.isInViewport) {
      this.playVisibleVideos();
    }
  }

  updateARIA(currentIndex) {
    const progressBars = this.querySelectorAll('[role="progressbar"]');
    progressBars.forEach((bar, idx) => {
      if (idx + 1 === currentIndex) {
        bar.setAttribute('aria-valuenow', '0');
      } else {
        bar.setAttribute('aria-valuenow', '100');
      }
    });
  }

  updateTabIndex() {
    const slides = this.querySelectorAll('.split-content__item');
    
    slides.forEach((slide) => {
      // Check if slide is visible via CSS
      const computedStyle = window.getComputedStyle(slide);
      const isVisible = computedStyle.visibility !== 'hidden' && 
                      computedStyle.display !== 'none';
      
      const mediaWithoutControls = slide.querySelectorAll(
        'video:not([controls]), audio:not([controls]), iframe'
      );
      
      mediaWithoutControls.forEach(el => {
        el.setAttribute('tabindex', '-1');
      });
      
      // Find focusable interactive elements
      const allElements = slide.querySelectorAll(
        'a[href], button, input, select, textarea, video[controls], audio[controls]'
      );
      
      const focusableElements = Array.from(allElements).filter(el => !el.disabled);
      
      if (isVisible) {
        // Slide is visible - make elements focusable
        focusableElements.forEach(el => {
          if (el.getAttribute('tabindex') === '-1') {
            el.removeAttribute('tabindex');
          }
        });
      } else {
        // Slide is hidden - block ALL elements
        focusableElements.forEach(el => {
          el.setAttribute('tabindex', '-1');
        });
      }
    });
  }

  updateSlideVisibility(currentPage) {
    const slides = this.querySelectorAll('.split-content__item');
    if (slides.length === 0) return;
    
    // Prevent excessive calls - only update if page changed
    if (this.lastVisibilityUpdate === currentPage) {
      return;
    }
    this.lastVisibilityUpdate = currentPage;
    
    // Determine columns from layout class
    let gridColumns = 1;
    
    if (this.classList.contains('layout--2')) {
      gridColumns = 2;
    } else if (this.classList.contains('layout--3')) {
      gridColumns = 3;
    }
    
    // Override based on viewport width
    if (window.innerWidth < 750) {
      gridColumns = 1;
    } else if (window.innerWidth < 1024) {
      if (!this.classList.contains('layout--3')) {
        gridColumns = Math.min(gridColumns, 2);
      }
    }
    
    slides.forEach((slide, idx) => {
      const slideNumber = idx + 1;
      
      // Calculate visible range
      const startSlide = currentPage;
      const endSlide = currentPage + gridColumns - 1;
      
      if (slideNumber >= startSlide && slideNumber <= endSlide) {
        slide.style.visibility = 'visible';
      } else {
        slide.style.visibility = 'hidden';
      }
    });
  }

  cleanup() {
    if (this.sectionObserver) {
      this.sectionObserver.disconnect();
      this.sectionObserver = null;
    }
    
    if (this.sliderComponent) {
      this.sliderComponent.removeEventListener('slideChanged', this.handleSlideChanged);
    }
    
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('resize', this.handleResize);
    
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    
    this.videoElements.forEach(videoData => {
      if (videoData.type === 'native') {
        videoData.element.pause();
        videoData.element.removeEventListener('ended', this.handleVideoEnded);
      }
    });
  }

  disconnectedCallback() {
    this.cleanup();
  }
}

if (!window.customElements.get("split-content")) {
  window.customElements.define("split-content", SplitContent);
}

})();